import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "../../api/endpoints";
import { useSession } from "../../hooks/useSession";
import { ModalBackdrop } from "../../components/ModalBackdrop";
import { IcoClose, IcoPlus, IcoTrash } from "../../components/icons";
import type {
  PermissionSchemaItem,
  Permissions,
  Role,
  ResourceAction,
  ScopedAction,
  AdminAction,
  Resource,
} from "../../types/domain";
import { ApiError } from "../../api/client";

// Пустая матрица — все false. Используется как стартовый шаблон при создании
// новой роли. Юзер сам прокликает нужные.
function emptyPermissions(): Permissions {
  return {
    launches: { view: false, create: false, edit: false, delete: false, viewOwn: false },
    cards: { view: false, create: false, edit: false, delete: false, viewOwn: false },
    landings: { view: false, create: false, edit: false, delete: false, viewOwn: false },
    tasks: { view: false, create: false, edit: false, delete: false, viewOwn: false },
    templates: { view: false, create: false, edit: false, delete: false, viewOwn: false },
    cardTypes: { view: false, create: false, edit: false, delete: false, viewOwn: false },
    launchTypes: { view: false, create: false, edit: false, delete: false, viewOwn: false },
    taskTypes: { view: false, create: false, edit: false, delete: false, viewOwn: false },
    admin: { manageUsers: false, manageRoles: false, viewAudit: false },
  };
}

function clonePermissions(p: Permissions): Permissions {
  return {
    launches: { ...p.launches, viewOwn: p.launches.viewOwn ?? false },
    cards: { ...p.cards, viewOwn: p.cards.viewOwn ?? false },
    landings: { ...p.landings, viewOwn: p.landings.viewOwn ?? false },
    tasks: { ...p.tasks, viewOwn: p.tasks.viewOwn ?? false },
    templates: { ...p.templates, viewOwn: p.templates.viewOwn ?? false },
    cardTypes: { ...p.cardTypes, viewOwn: p.cardTypes.viewOwn ?? false },
    launchTypes: { ...p.launchTypes, viewOwn: p.launchTypes.viewOwn ?? false },
    taskTypes: { ...p.taskTypes, viewOwn: p.taskTypes.viewOwn ?? false },
    admin: { ...p.admin },
  };
}

/** Поддерживается ли scoped-action `viewOwn` для конкретного ресурса. */
function supportsViewOwn(resource: Resource): boolean {
  return resource === "tasks" || resource === "cards";
}

export function AdminRolesPage() {
  const me = useSession().data ?? null;
  const qc = useQueryClient();

  const rolesQuery = useQuery({
    queryKey: ["admin-roles"],
    queryFn: async () => (await adminApi.listRoles()).roles,
  });
  const schemaQuery = useQuery({
    queryKey: ["admin-permissions-schema"],
    queryFn: async () => (await adminApi.permissionsSchema()).schema,
    staleTime: Infinity,
  });

  const [editing, setEditing] = useState<Role | "new" | null>(null);
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const removeMutation = useMutation({
    mutationFn: (id: string) => adminApi.removeRole(id),
    onSuccess: () => {
      setPendingRemoveId(null);
      setRemoveError(null);
      qc.invalidateQueries({ queryKey: ["admin-roles"] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.code === "role_in_use") {
        setRemoveError("Эта роль ещё используется — сначала переведите пользователей на другую.");
      } else {
        setRemoveError(err instanceof Error ? err.message : "Не удалось удалить");
      }
    },
  });

  const pendingRemove = rolesQuery.data?.find((r) => r.id === pendingRemoveId) ?? null;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Роли и права</h1>
          <div className="admin-sub">
            Встроенные роли защищены от изменения. Создавайте свои с произвольной матрицей
            действий — без программиста.
          </div>
        </div>
        <button type="button" className="btn-primary" onClick={() => setEditing("new")}>
          <IcoPlus size={14} /> <span>Создать роль</span>
        </button>
      </div>

      {rolesQuery.isLoading && <div className="admin-empty">Загружаем…</div>}
      {rolesQuery.isError && (
        <div className="admin-empty admin-error">
          Не удалось загрузить роли. {String(rolesQuery.error)}
        </div>
      )}

      {rolesQuery.data && (
        <div className="admin-table">
          <div className="admin-row admin-row--roles admin-row-head">
            <div>Роль</div>
            <div>Описание</div>
            <div style={{ textAlign: "right" }}>Юзеров</div>
            <div />
          </div>
          {rolesQuery.data.map((r) => (
            <div key={r.id} className="admin-row admin-row--roles admin-row-user">
              <div className="admin-user-cell" style={{ alignItems: "center" }}>
                <div>
                  <div className="admin-user-name">
                    {r.name}
                    {r.isBuiltin && <span className="admin-self-tag">встроенная</span>}
                  </div>
                  <div className="admin-user-email" style={{ fontFamily: "monospace" }}>
                    {r.id}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.4 }}>
                {r.description ?? <span style={{ color: "var(--text-3)" }}>—</span>}
              </div>
              <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {r.usageCount ?? 0}
              </div>
              <div className="admin-actions-cell">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => setEditing(r)}
                  title={r.isBuiltin ? "Просмотр матрицы прав" : "Редактировать"}
                >
                  {r.isBuiltin ? "Открыть" : "Редактировать"}
                </button>
                {!r.isBuiltin && (
                  <button
                    type="button"
                    className="admin-remove-btn"
                    onClick={() => {
                      setPendingRemoveId(r.id);
                      setRemoveError(null);
                    }}
                    disabled={(r.usageCount ?? 0) > 0}
                    title={
                      (r.usageCount ?? 0) > 0
                        ? "Роль используется — переведите пользователей на другую"
                        : "Удалить роль"
                    }
                  >
                    <IcoTrash size={12} /> <span>Удалить</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && schemaQuery.data && (
        <RoleEditorModal
          schema={schemaQuery.data}
          role={editing === "new" ? null : editing}
          isSelfRole={editing !== "new" && me?.role === editing.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["admin-roles"] });
            // Сбрасываем сессию — у текущего пользователя могла поменяться
            // его собственная матрица.
            qc.invalidateQueries({ queryKey: ["session"] });
          }}
        />
      )}

      {pendingRemove && (
        <ConfirmRemoveRole
          role={pendingRemove}
          busy={removeMutation.isPending}
          error={removeError}
          onCancel={() => {
            setPendingRemoveId(null);
            setRemoveError(null);
          }}
          onConfirm={() => removeMutation.mutate(pendingRemove.id)}
        />
      )}
    </div>
  );
}

interface RoleEditorModalProps {
  schema: PermissionSchemaItem[];
  /** null → создание новой. */
  role: Role | null;
  isSelfRole: boolean;
  onClose: () => void;
  onSaved: () => void;
}

function RoleEditorModal({ schema, role, isSelfRole, onClose, onSaved }: RoleEditorModalProps) {
  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [perms, setPerms] = useState<Permissions>(() =>
    role ? clonePermissions(role.permissions) : emptyPermissions(),
  );
  const [error, setError] = useState<string | null>(null);

  const isBuiltin = role?.isBuiltin === true;
  const isView = isBuiltin; // встроенные — read-only

  // Если у роли поменялась prop (например, после save), reset state.
  useEffect(() => {
    if (role) {
      setName(role.name);
      setDescription(role.description ?? "");
      setPerms(clonePermissions(role.permissions));
    }
  }, [role?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCheck = (
    resource: Resource | "admin",
    action: ResourceAction | ScopedAction | AdminAction,
    val: boolean,
  ) => {
    setPerms((p) => {
      const next = clonePermissions(p);
      if (resource === "admin") {
        next.admin[action as AdminAction] = val;
      } else if (action === "viewOwn") {
        next[resource].viewOwn = val;
      } else {
        next[resource][action as ResourceAction] = val;
      }
      return next;
    });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      adminApi.createRole({
        name: name.trim(),
        description: description.trim() || null,
        permissions: perms,
      }),
    onSuccess: () => onSaved(),
    onError: (err: unknown) => setError(err instanceof Error ? err.message : "Ошибка"),
  });

  const patchMutation = useMutation({
    mutationFn: () => {
      if (!role) throw new Error("no role");
      return adminApi.patchRole(role.id, {
        name: name.trim(),
        description: description.trim() || null,
        permissions: perms,
      });
    },
    onSuccess: () => onSaved(),
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.code === "cannot_demote_self") {
        setError("Нельзя отнять у своей роли admin.manageRoles или admin.manageUsers.");
      } else if (err instanceof ApiError && err.code === "builtin_immutable") {
        setError("Встроенную роль изменить нельзя.");
      } else {
        setError(err instanceof Error ? err.message : "Ошибка");
      }
    },
  });

  const isPending = createMutation.isPending || patchMutation.isPending;
  const canSave = !isView && name.trim().length > 0;

  const title = role ? (isBuiltin ? `Просмотр: ${role.name}` : `Редактирование: ${role.name}`) : "Новая роль";

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        className="modal"
        style={{ width: 760, maxHeight: "90vh", display: "flex", flexDirection: "column" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h2>{title}</h2>
          <div className="spacer" />
          <button type="button" className="modal-close" onClick={onClose}>
            <IcoClose size={16} />
          </button>
        </div>
        <div className="modal-body" style={{ overflow: "auto" }}>
          {isSelfRole && !isBuiltin && (
            <div
              className="admin-error"
              style={{ marginBottom: 12, background: "#FEF3C7", color: "#92400E" }}
            >
              Это ваша роль. Снять admin.manageRoles или admin.manageUsers с самого себя нельзя
              — мы откажем при сохранении.
            </div>
          )}
          <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
            <input
              className="form-input"
              placeholder="Название (например: Маркетолог)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isView}
              maxLength={120}
            />
            <textarea
              className="form-input"
              placeholder="Описание роли (необязательно)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isView}
              rows={2}
              maxLength={500}
              style={{ resize: "vertical" }}
            />
          </div>
          <PermissionsMatrix schema={schema} perms={perms} disabled={isView} onToggle={setCheck} />
          {error && (
            <div className="admin-error" style={{ marginTop: 12 }}>
              {error}
            </div>
          )}
        </div>
        <div className="modal-foot">
          <div className="spacer" />
          <button type="button" className="btn-ghost" onClick={onClose}>
            {isView ? "Закрыть" : "Отмена"}
          </button>
          {!isView && (
            <button
              type="button"
              className="btn-primary"
              disabled={!canSave || isPending}
              onClick={() => {
                setError(null);
                if (role) patchMutation.mutate();
                else createMutation.mutate();
              }}
            >
              {isPending ? "Сохраняем…" : role ? "Сохранить" : "Создать"}
            </button>
          )}
        </div>
      </div>
    </ModalBackdrop>
  );
}

interface PermissionsMatrixProps {
  schema: PermissionSchemaItem[];
  perms: Permissions;
  disabled: boolean;
  onToggle: (
    resource: Resource | "admin",
    action: ResourceAction | ScopedAction | AdminAction,
    val: boolean,
  ) => void;
}

function PermissionsMatrix({ schema, perms, disabled, onToggle }: PermissionsMatrixProps) {
  // 5 колонок: Просмотр / Только свои / Создание / Редактирование / Удаление.
  // «Только свои» — scoped-action, работает для tasks и cards; в строках
  // других ресурсов чекбокс отключён с подсказкой.
  const resourceItems = useMemo(() => schema.filter((s) => s.resource !== "admin"), [schema]);
  const adminItem = useMemo(() => schema.find((s) => s.resource === "admin") ?? null, [schema]);

  return (
    <>
      <div className="perm-table perm-table--with-scope" role="grid">
        <div className="perm-row perm-row-head">
          <div>Ресурс</div>
          <div>Просмотр</div>
          <div title="Для задач и карточек: пользователь видит только свои сущности.">
            Только свои
          </div>
          <div>Создание</div>
          <div>Редактирование</div>
          <div>Удаление</div>
        </div>
        {resourceItems.map((item) => {
          const r = item.resource as Resource;
          const sub = perms[r];
          const ownEnabled = supportsViewOwn(r);
          return (
            <div key={r} className="perm-row">
              <div className="perm-resource">{item.label}</div>
              <div>
                <label className="perm-check">
                  <input
                    type="checkbox"
                    checked={sub.view === true}
                    disabled={disabled}
                    onChange={(e) => onToggle(r, "view", e.target.checked)}
                  />
                </label>
              </div>
              <div>
                <label
                  className="perm-check"
                  title={
                    ownEnabled
                      ? r === "tasks"
                        ? "Видит только свои задачи (автор или ответственный)"
                        : "Видит только свои карточки (создатель)"
                      : "Сейчас доступно только для задач и карточек"
                  }
                >
                  <input
                    type="checkbox"
                    checked={ownEnabled && sub.viewOwn === true}
                    disabled={disabled || !ownEnabled}
                    onChange={(e) => onToggle(r, "viewOwn", e.target.checked)}
                  />
                </label>
              </div>
              {(["create", "edit", "delete"] as ResourceAction[]).map((a) => (
                <div key={a}>
                  <label className="perm-check">
                    <input
                      type="checkbox"
                      checked={sub[a] === true}
                      disabled={disabled}
                      onChange={(e) => onToggle(r, a, e.target.checked)}
                    />
                  </label>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {adminItem && (
        <div className="perm-table perm-table-admin" style={{ marginTop: 18 }}>
          <div className="perm-row perm-row-head">
            <div>Администрирование</div>
            {adminItem.actions.map((a) => (
              <div key={a.key}>{a.label}</div>
            ))}
          </div>
          <div className="perm-row">
            <div className="perm-resource">{adminItem.label}</div>
            {adminItem.actions.map((a) => (
              <div key={a.key}>
                <label className="perm-check">
                  <input
                    type="checkbox"
                    checked={perms.admin[a.key as AdminAction] === true}
                    disabled={disabled}
                    onChange={(e) => onToggle("admin", a.key as AdminAction, e.target.checked)}
                  />
                </label>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

interface ConfirmRemoveRoleProps {
  role: Role;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

function ConfirmRemoveRole({ role, busy, error, onCancel, onConfirm }: ConfirmRemoveRoleProps) {
  return (
    <ModalBackdrop onClose={onCancel}>
      <div className="modal" style={{ width: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="launch-dot" style={{ background: "#FEE4E2" }}>
            <IcoTrash size={12} stroke="#DC2626" />
          </div>
          <h2>Удалить роль?</h2>
          <div className="spacer" />
          <button type="button" className="modal-close" onClick={onCancel}>
            <IcoClose size={16} />
          </button>
        </div>
        <div className="modal-body">
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-2)", lineHeight: 1.5 }}>
            Роль <b>{role.name}</b> будет удалена. Если её ещё используют пользователи —
            сначала переведите их на другую.
          </p>
          {error && (
            <div className="admin-error" style={{ marginTop: 10 }}>
              {error}
            </div>
          )}
        </div>
        <div className="modal-foot">
          <div className="spacer" />
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>
            Отмена
          </button>
          <button type="button" className="btn-danger" onClick={onConfirm} disabled={busy}>
            {busy ? "Удаляем…" : "Удалить роль"}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
